// Simple Appwrite database connectivity ping using Health API
// Calls /health/db to verify the server's DB connection is healthy.

export type DbPingResult = {
  ok: boolean;
  data?: any;
  error?: string;
  statusCode?: number;
  reachable?: boolean; // true when endpoint+project header recognized (e.g., 401 unauthorized)
};

const endpoint = import.meta.env.VITE_APPWRITE_ENDPOINT as string;
const projectId = import.meta.env.VITE_APPWRITE_PROJECT_ID as string;
const normalizedEndpoint = (endpoint || '').replace(/\/+$/, '');

export const pingAppwriteDb = async (): Promise<DbPingResult> => {
  if (!normalizedEndpoint) {
    return { ok: false, error: 'Missing VITE_APPWRITE_ENDPOINT' };
  }
  try {
    const res = await fetch(`${normalizedEndpoint}/health/db`, {
      method: 'GET',
      headers: projectId ? { 'X-Appwrite-Project': projectId } : {},
    });
    const statusCode = res.status;
    let data: any = null;
    try {
      data = await res.json();
    } catch {}

    // Healthy when DB is connected (200) and status reports connected
    const healthy = res.ok && (data?.status === true || data?.status === 'connected' || data?.database === true);
    // Reachable when server recognizes project header but requires auth (common from browsers)
    const unauthorizedButReachable = statusCode === 401 && (data?.type === 'general_unauthorized_scope' || /unauthorized/i.test(data?.message || ''));

    if (healthy) {
      return { ok: true, data, statusCode, reachable: true };
    }
    if (unauthorizedButReachable) {
      return { ok: false, data, statusCode, reachable: true, error: data?.message || 'Unauthorized (expected from browser). Add API key or call from server.' };
    }
    return { ok: false, data, statusCode, reachable: false, error: data?.message || `HTTP ${statusCode}` };
  } catch (e: any) {
    return { ok: false, error: e?.message ?? String(e) };
  }
};